import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCIcon, RCCRefModalProps, RCCServicesApi } from "react-common-components";
// import List from "src/components/List";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { IIndicatorsLevel } from "../interface";
import { colLevel } from "../ListIndicators";
import { IDetailsIndicator, IRefActionsModalDetailsIndicators } from "./interface";
import SpanText, { DivCenter, UlStyled } from "./styles";

const levelText = (level: IIndicatorsLevel, translate: (text: string) => string) => {
	if(level === IIndicatorsLevel.OPERATIONAL)
		return translate("Operacional");
	if(level === IIndicatorsLevel.STRATEGIC)
		return translate("Estratégico");

	return translate("Tático");
};

const ModalIndicatorDetails: React.ForwardRefRenderFunction<IRefActionsModalDetailsIndicators> = (_, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;

	// Modal Open Details
	const modalOpenDetails = useRef<RCCRefModalProps>(null);
	const [detailsIndicator, setDetailsIndicator] = useState<IDetailsIndicator | undefined>(undefined);
	const [subtitle, setSubtitle] = useState("");
	const actionButtonOpenDetailsIndicators = async (id: number, title: string) => {
		try{
			setSubtitle(title);
			setLoading(true);
			
			const {data} = await RCCServicesApi.getRequest({
				method: "Indicators/GetIndicatorDetails",
				urlParameters: {
					id
				}
			});
			
			setDetailsIndicator(data as IDetailsIndicator);

			setLoading(false);
			modalOpenDetails.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os detalhes dos indicadores"), addToast);
			setLoading(false);
		}
	};

	useImperativeHandle(ref, () => ({
		actionButtonOpenDetailsIndicators
	}));
    
	if(!detailsIndicator)
		return null;

	return (
		<>		
			{/* Modal Details INDICATOR */}
			<Modal
				ref={modalOpenDetails}
				header={{
					title: translate("Detalhes do indicador"),
					subtitle,
					icon: {
						name: "info"
					}
				}}
				footer= {{
					showSaveButton: false,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
				styleInline={ModalSide}
				withoutForm
			>
				<RCCGrid
					container
					item
					alignItems="center"
					justifyContent="space-between"
				>
					<DivCenter>
						<Title size="sm" color="purple">
							{translate("Nível")}
						</Title>
						{colLevel(detailsIndicator.iqc001Nivel, translate)} 
						<SpanText>{levelText(detailsIndicator.iqc001Nivel, translate)}</SpanText>
					</DivCenter>
					{detailsIndicator.iqc001Validade !== null && (
						<DivCenter>
							<Title size="sm" color="purple">
								{translate("Validade")}
							</Title>
							<SpanText>{formatDate(detailsIndicator.iqc001Validade,currentLanguage)}</SpanText>
						</DivCenter>
					)}
				</RCCGrid>

				<RCCGrid
					container
					item
					alignItems="center"
					justifyContent="space-between"
				>
					<DivCenter>
						<Title size="sm" color="purple">
							{translate("Forma de avaliação")}
						</Title>
						<RCCIcon
							name={detailsIndicator.iqc001Formaaval === 0 ? "arrowDown" : "arrowUp"}
							size={36}
							color="var(--purple-lighter)"
							style={{
								border: "2px solid var(--purple-lighter)",
								borderRadius: "50%",
								marginRight: "4px"
							}}
						/>
						<SpanText>{detailsIndicator.iqc001Formaaval === 0 ? translate("Melhor para baixo") : translate("Melhor para cima")}</SpanText>
					</DivCenter>

					<DivCenter>
						<Title size="sm" color="purple">
							{translate("Responsável")}
						</Title>
						<Photo 
							hover={false}
							name={detailsIndicator.iqc001Efa005Nome}
							link={detailsIndicator.iqc001Efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${detailsIndicator.iqc001Efa005Imagem}`}
							width={36}
							height={36}
						/>
						<SpanText>
							{detailsIndicator.iqc001Efa005Nome}
						</SpanText>
					</DivCenter>
				</RCCGrid>

				<RCCGrid
					container
					item
					alignItems="center"
					justifyContent="space-between"
				>
					<DivCenter>
						<Title size="sm" color="purple">
							{translate("Setor")}
						</Title>
						<SpanText>{detailsIndicator.iqc001Taa001Nome}</SpanText>
					</DivCenter>
					{detailsIndicator.iqc001Taa002Nome !== null && (
						<DivCenter>
							<Title size="sm" color="purple">
								{translate("Classificação")}
							</Title>
							<SpanText>{detailsIndicator.iqc001Taa002Nome}</SpanText>
						</DivCenter>
					)}
				</RCCGrid>

				<br />
				<hr />
				<Title size="sm" color="purple">
					{translate("Método de avaliação")}
				</Title>
				<SpanText>
					{detailsIndicator.iqc001Metodo !== null ? detailsIndicator.iqc001Metodo : `${translate("Método de avaliação")} ${translate("não foi informado")}`}
				</SpanText>
				<Title size="sm" color="purple" marginTop={16}>
					{translate("Objetivo")}
				</Title>
				<SpanText>
					{detailsIndicator.iqc001Descobjetivo !== null ? detailsIndicator.iqc001Descobjetivo : `${translate("Objetivo")} ${translate("não foi informado")}`}
				</SpanText>
				<Title size="sm" color="purple" marginTop={16}>
					{translate("Observação")}
				</Title>
				<SpanText>
					{detailsIndicator.iqc001Observacao !== null ? detailsIndicator.iqc001Observacao : `${translate("Observação")} ${translate("não foi informado")}`}
				</SpanText>
				<hr />
				<br />

				<RCCGrid
					container
					item
					alignItems="center"
					justifyContent="space-between"
				>
					<DivCenter>
						<Title size="sm" color="purple">
							{translate("Meta melhoria")}
						</Title>
						<SpanText>({detailsIndicator.goal.iqtbb001.iqb001Codigo}) {detailsIndicator.goal.iqtbb001.iqb001Descricao}</SpanText>
					</DivCenter>
					{detailsIndicator.competitiveness !== null && (
						<DivCenter>
							<Title size="sm" color="purple">
								{translate("Meta competitividade")}
							</Title>
							<SpanText>({detailsIndicator.competitiveness.iqtbb001.iqb001Codigo}) {detailsIndicator.competitiveness.iqtbb001.iqb001Descricao}</SpanText>
						</DivCenter>
					)}
					{detailsIndicator.compromise !== null && (
						<DivCenter>
							<Title size="sm" color="purple">
								{translate("Meta compromisso")}
							</Title>
							<SpanText>({detailsIndicator.compromise.iqtbb001.iqb001Codigo}) {detailsIndicator.compromise.iqtbb001.iqb001Descricao}</SpanText>
						</DivCenter>
					)}
				</RCCGrid>

				<Title size="sm" color="purple" marginTop={16}>
					{translate("Outros campos")}
				</Title>
				<UlStyled>
					{detailsIndicator.fields.map((field) => (
						<li key={`indicator-details-other-fields-${field.iqtbb001.iqb001Codigo}`}>({field.iqtbb001.iqb001Codigo}) {field.iqtbb001.iqb001Descricao}</li>
					))}
				</UlStyled>
				
			</Modal>
		</>
	);
};

export default forwardRef(ModalIndicatorDetails);
